import React from "react";
import styled from "styled-components";

const AdminNavigationTabs = React.forwardRef(function AdminNavigationTabs(
  {
    children,
    className,
    orientation = "horizontal",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...nativeProps
  },
  ref
) {
  const handleNavigationKeyDown = (event) => {
    const previousKey =
      orientation === "vertical"
        ? "ArrowUp"
        : "ArrowLeft";

    const nextKey =
      orientation === "vertical"
        ? "ArrowDown"
        : "ArrowRight";

    if (
      ![
        previousKey,
        nextKey,
        "Home",
        "End",
      ].includes(event.key)
    ) {
      return;
    }

    const tabList =
      event.currentTarget;

    const tabs = Array.from(
      tabList.querySelectorAll(
        '[role="tab"]',
      ),
    ).filter(
      (tab) =>
        !tab.disabled
        && tab.getAttribute(
          "aria-disabled",
        ) !== "true",
    );

    if (tabs.length === 0) {
      return;
    }

    const currentIndex =
      tabs.indexOf(
        document.activeElement,
      );

    let nextIndex;

    if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex =
        tabs.length - 1;
    } else if (
      event.key === nextKey
    ) {
      nextIndex =
        currentIndex < 0
          ? 0
          : (
              currentIndex + 1
            ) % tabs.length;
    } else {
      nextIndex =
        currentIndex < 0
          ? tabs.length - 1
          : (
              currentIndex
              - 1
              + tabs.length
            ) % tabs.length;
    }

    event.preventDefault();

    tabs[nextIndex].focus();
  };

  return (
    <TabsContainer
      {...nativeProps}
      ref={ref}
      role="tablist"
      onKeyDown={handleNavigationKeyDown}
      className={className}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-orientation={orientation}
      $orientation={orientation}
    >
      {children}
    </TabsContainer>
  );
});

AdminNavigationTabs.displayName = "AdminNavigationTabs";

const TabsContainer = styled.div`
  display: flex;
  flex-direction:
    ${({ $orientation }) =>
      $orientation === "vertical"
        ? "column"
        : "row"};

  align-items:
    ${({ $orientation }) =>
      $orientation === "vertical"
        ? "stretch"
        : "center"};

  gap: 0.5rem;
  width: 100%;

  overflow-x:
    ${({ $orientation }) =>
      $orientation === "vertical"
        ? "visible"
        : "auto"};

  overflow-y:
    ${({ $orientation }) =>
      $orientation === "vertical"
        ? "auto"
        : "visible"};

  scrollbar-width: thin;

  ${({ $orientation }) =>
    $orientation === "vertical"
      ? `
        padding: 0.35rem;

        border:
          1px solid
          rgba(255, 255, 255, 0.06);

        border-radius:
          14px;

        background:
          rgba(7, 15, 35, 0.28);

        > [role="tab"] {
          width: 100%;
          justify-content: flex-start;
          text-align: left;

          min-height: 42px;

          padding:
            0.65rem
            0.75rem;

          border:
            1px solid
            transparent;

          border-radius:
            10px;

          background:
            transparent;

          color:
            rgba(247, 247, 247, 0.72);

          box-shadow:
            none;

          font-weight:
            700;
        }

        > [role="tab"]:hover:not(:disabled) {
          background:
            rgba(255, 255, 255, 0.05);

          border-color:
            rgba(255, 255, 255, 0.08);

          color:
            rgba(247, 247, 247, 0.92);
        }

        > [role="tab"][aria-selected="true"] {
          background:
            rgba(37, 99, 235, 0.14);

          border-color:
            rgba(59, 130, 246, 0.34);

          color:
            #f7f7f7;

          box-shadow:
            inset 3px 0 0
            rgba(96, 165, 250, 0.95);
        }

        > [role="tab"][aria-selected="true"]:hover:not(:disabled) {
          background:
            rgba(37, 99, 235, 0.18);
        }
      `
      : ""}
`;

export default AdminNavigationTabs;
